import React from "react";
import { graphql, Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import {
  Container,
  Header,
  Segment,
  Grid,
  Label,
  List,
  Icon,
  Message,
} from "semantic-ui-react";

import { MDXProvider } from "@mdx-js/react";
import { MDXRenderer } from "gatsby-plugin-mdx";

import _ from "lodash";

import Layout from "../components/layout";
import SEO from "../components/seo";
import Observation from "../components/Observation";

import {
  specVersionLabel,
  formatSpecSection,
  getSpecSectionTitle,
} from "../components/spec_utils";
import pytamaroLogo from "../images/PyTamaro-horizontal.png";

import EmbedYoutube from "../components/EmbedYoutube";
import ExpressionTreeViewer from "../components/ExpressionTreeViewer";
import WorkerSandbox from "../components/WorkerSanbox";
import ScratchBlocksMDX from "../components/ScratchBlocksMDX";

const shortcodes = {
  EmbedYoutube,
  ExpressionTreeViewer,
  WorkerSandbox,
  ScratchBlocksMDX,
};

// reference: the substructure from the misconception file's frontmatter
// refs: query result (all edges for documents with frontmatter.type == "ref")
function LiteratureReference({ reference, misconception, refs, zoteroItems }) {
  const refEdge = refs.edges.find(
    edge => edge.node.slug === "references/" + reference.fields.citationKey
  );
  const refNode = refEdge ? refEdge.node : undefined;
  const zoteroItem = zoteroItems
    ? zoteroItems.nodes.find(
        node => node.citationKey === reference.fields.citationKey
      )
    : undefined;
  const header = zoteroItem
    ? zoteroItem.data
      ? zoteroItem.data.creatorSummary +
        " " +
        zoteroItem.data.parsedDate.substring(0, 4)
      : reference.fields.citationKey
    : reference.fields.citationKey;
  const phenomenaToShow =
    reference.frontmatter.phenomenonKinds
      .flatMap(pk => pk.phenomena || [])
      .filter(ph => ph.plMisconceptions)
      .filter(ph =>
        ph.plMisconceptions.some(
          plm =>
            plm.name === misconception.name && plm.lang === misconception.lang
        )
      )
      .map(p => p.id) || [];
  return (
    <List.Item
      as={Link}
      to={`/reference/${reference.fields.citationKey}`}
      //to={reference.fields.slug}
      // href={`https://www.zotero.org/groups/2535417/progmiscon/items/${zoteroReferences[reference.citationKey]}`}
      key={`zotero-${reference.fields.citationKey}`}
    >
      <List.Icon
        className='file alternate outline'
        size='large'
        verticalAlign='top'
      />
      <List.Content>
        <List.Header>
          {/* color this like a link, without producing a nested a element */}
          <span
            style={{
              fontSize: "110%",
              color: "#4183c4",
              wordBreak: "break-all",
            }}
          >
            {header}
          </span>
        </List.Header>
        <List.Description>
          {refNode ? (
            <>
              <br></br>
              <em>{refNode.frontmatter.method}</em>
              <br />
              <br />
              {refNode.frontmatter.subjects}
              <br />
              <br />

              {refNode.frontmatter.langs.map(lang => (
                <Label key={lang} color='red'>
                  {lang}
                </Label>
              ))}
              {refNode.frontmatter.artifact &&
              refNode.frontmatter.artifact.url ? (
                <span
                  style={{ marginLeft: "1rem" }}
                  data-tooltip='Artifact supporting the paper is available'
                >
                  <Icon className='brown archive' />
                  Artifact
                </span>
              ) : null}
              <PhenomenonKinds
                phenomenonKinds={refNode.frontmatter.phenomenonKinds}
                phenomenaToShow={phenomenaToShow}
              />
            </>
          ) : null}
        </List.Description>
      </List.Content>
    </List.Item>
  );
}

// render the kinds of phenomena, and for each kind, all the phenomena
function PhenomenonKinds({ phenomenonKinds, phenomenaToShow }) {
  return (
    <div>
      {phenomenonKinds
        ? phenomenonKinds.map(kind => (
            <div key={kind.name}>
              <br />
              <strong data-tooltip={kind.description}>{kind.name}</strong>{" "}
              studied:
              <List>
                {kind.phenomena
                  ? kind.phenomena.map(phenomenon =>
                      phenomenaToShow &&
                      phenomenaToShow.includes(phenomenon.id) ? (
                        <Phenomenon key={phenomenon.id} p={phenomenon} />
                      ) : null
                    )
                  : null}
              </List>
            </div>
          ))
        : null}
    </div>
  );
}

function Phenomenon({ p }) {
  return (
    <List.Item>
      <List.Content>
        <strong>{p.id}</strong>: {p.name}
        {p.observations
          ? p.observations.map(o => <Observation key={o.measure} o={o} />)
          : null}
      </List.Content>
    </List.Item>
  );
}

function EvidenceTag({ observed_level, pl }) {
  let label = "Observed";
  let tooltip =
    "This misconception has been observed in some real-world situation (e.g., in a classroom)";

  if (observed_level === "no") {
    label = "Not Yet Observed";
    tooltip =
      "This misconception has not been observed in " +
      pl +
      " yet (e.g., it is theoretically derived from observations in other programming languages)";
  } else if (observed_level === "research") {
    label = "Observed in Published Research";
    tooltip =
      "At least one published research study listed under Literature References provides evidence of this " +
      pl +
      " misconception";
  }
  return (
    <Label color='black' data-tooltip={tooltip}>
      {label}
    </Label>
  );
}

export default function MisconceptionPageTemplate({ data }) {
  const { mdx, specsMdx, concepts, refs, zoteroItems } = data;

  const misconceptionConcepts = mdx.frontmatter.concepts
    ? mdx.frontmatter.concepts.filter(c => _.find(concepts.nodes, { name: c }))
    : [];

  const thisLang = mdx.fields.pl;
  const thisName = mdx.fields.name;

  const relatedRefs = refs.edges
    .map(e => e.node)
    .filter(refNode => refNode.frontmatter.phenomenonKinds)
    .filter(refNode =>
      refNode.frontmatter.phenomenonKinds
        .filter(pk => pk.phenomena)
        .some(pk =>
          pk.phenomena
            .filter(p => p.plMisconceptions)
            .some(p =>
              p.plMisconceptions.some(
                plm => plm.lang === thisLang && plm.name === thisName
              )
            )
        )
    );

  return (
    <Layout>
      <SEO
        title={mdx.fields.name}
        cardTitle={`Misconception: ${mdx.fields.name}`}
        imageRelativeUrl={
          mdx.fields.image ? mdx.fields.image.childImageSharp.resize.src : null
        }
        description={mdx.frontmatter.shortDescription}
        useLargeImageCard={true}
      />
      <Container
        style={{
          marginTop: "100px",
          marginBottom: "100px",
          background: "#faefdb",
        }}
      >
        <Segment inverted color='red' vertical style={{ padding: "1em 1em" }}>
          <Header as='h1' inverted>
            <Header.Content
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "4px",
                marginBottom: "4px",
                gap: "0.5em",
              }}
            >
              <span style={{ lineHeight: "1" }}>{mdx.fields.name}</span>
              {mdx.frontmatter.status === "deprecated" ? (
                <Label color='black'>Deprecated</Label>
              ) : null}
              {mdx.frontmatter.status === "draft" ? (
                <Label color='yellow'>DRAFT</Label>
              ) : null}
              <EvidenceTag
                observed_level={mdx.frontmatter.observed}
                pl={mdx.fields.pl}
              />
            </Header.Content>
          </Header>
        </Segment>
        <Grid className='relaxed'>
          <Grid.Row>
            <Grid.Column width={10} style={{ paddingRight: 0 }}>
              {mdx.fields.image ? (
                <GatsbyImage
                  image={mdx.fields.image.childImageSharp.gatsbyImageData}
                  className='ui image'
                  alt={mdx.fields.name}
                  style={{ width: "10242px" }}
                />
              ) : null}
              <div style={{ padding: "1em" }}>
                <Grid className='relaxed'>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <div
                        className='ui inverted raised red segment'
                        style={{ height: "100%" }}
                      >
                        <div className='ui header'>Incorrect</div>
                        <p
                          style={{
                            textWrap: "pretty",
                          }}
                        >
                          {mdx.frontmatter.shortDescription}
                        </p>
                      </div>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <div
                        className='ui inverted raised green segment'
                        style={{ height: "100%" }}
                      >
                        <div className='ui header'>Correct</div>
                        <p
                          style={{
                            textWrap: "pretty",
                          }}
                        >
                          {mdx.frontmatter.shortCorrection}
                        </p>
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </div>
              <Segment
                style={{
                  padding: "1em 1em",
                  rbackground: "#f2e3be",
                }}
                vertical
              >
                {mdx.frontmatter.pytamaroActivityVersion ? (
                  <Message icon info>
                    <img
                      className='ui image'
                      alt='PyTamaro Logo'
                      width='120'
                      src={pytamaroLogo}
                      style={{ marginRight: "20px" }}
                    />
                    <Message.Content>
                      <Message.Header>
                        <a
                          href={`https://pytamaro.si.usi.ch/activities/luce/mc-${mdx.fields.name}/en/${mdx.frontmatter.pytamaroActivityVersion}?curriculum=luce%2Fmisconceptions`}
                          // eslint-disable-next-line react/jsx-no-target-blank
                          target='_blank'
                          rel='noreferrer'
                        >
                          Experience {mdx.fields.name} in Your Browser
                        </a>
                      </Message.Header>
                    </Message.Content>
                  </Message>
                ) : null}
                {mdx.fields.correction &&
                mdx.fields.correction.childMdx.rawBody ? (
                  <>
                    <Header as='h2' dividing>
                      {/* 
                        Using className instead of name to avoid semantic-ui-react from 
                        complaining about unknown icon name. 
                        The "tools" icon does exist in fomantic-ui 2.8.
                      */}
                      <Icon className='tools' />
                      <Header.Content>
                        Correction
                        <Header.Subheader>
                          Here is what's right.
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                    <MDXProvider components={shortcodes}>
                      <MDXRenderer>
                        {mdx.fields.correction.childMdx.body}
                      </MDXRenderer>
                    </MDXProvider>
                  </>
                ) : null}
                {mdx.frontmatter.observed === "no" ||
                (mdx.fields.origin && mdx.fields.origin.childMdx.rawBody) ? (
                  <>
                    <Header as='h2' dividing>
                      <Icon className='map marker alternate' />
                      <Header.Content>
                        Origin
                        <Header.Subheader>
                          Where could this misconception come from?
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                    {mdx.frontmatter.observed === "no" ? (
                      <Segment
                        inverted
                        color='black'
                        vertical
                        style={{
                          padding: "1em 1em",
                          marginBottom: "1em",
                        }}
                      >
                        <b>NOTE &mdash;&nbsp;</b>
                        We are not aware of repeated observations of this
                        misconception in {mdx.fields.pl}. We documented it, for
                        completeness, based on observations of an equivalent
                        misconception in other languages.
                      </Segment>
                    ) : null}
                    {mdx.fields.origin && mdx.fields.origin.childMdx.rawBody ? (
                      <MDXProvider components={shortcodes}>
                        <MDXRenderer>
                          {mdx.fields.origin.childMdx.body}
                        </MDXRenderer>
                      </MDXProvider>
                    ) : null}
                  </>
                ) : null}
                {mdx.fields.symptoms && mdx.fields.symptoms.childMdx.rawBody ? (
                  <>
                    <Header as='h2' dividing>
                      <Icon name='heartbeat' />
                      <Header.Content>
                        Symptoms
                        <Header.Subheader>
                          How do you know your students might have this
                          misconception?
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                    <MDXProvider components={shortcodes}>
                      <MDXRenderer>
                        {mdx.fields.symptoms.childMdx.body}
                      </MDXRenderer>
                    </MDXProvider>
                  </>
                ) : null}

                {mdx.fields.value && mdx.fields.value.childMdx.rawBody ? (
                  <>
                    <Header as='h2' dividing>
                      <Icon className='hand holding heart' />
                      <Header.Content>
                        Value
                        <Header.Subheader>
                          How can you build on this misconception?
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                    <MDXProvider components={shortcodes}>
                      <MDXRenderer>
                        {mdx.fields.value.childMdx.body}
                      </MDXRenderer>
                    </MDXProvider>
                  </>
                ) : null}
              </Segment>
            </Grid.Column>
            <Grid.Column width={6} style={{ paddingLeft: 0 }}>
              <div
                style={{
                  padding: "1em",
                  background: "#faefdb",
                  rbackground: "#f2e3be",
                }}
              >
                {mdx.frontmatter.videoId ? (
                  <EmbedYoutube id={mdx.frontmatter.videoId} />
                ) : null}

                <Header as='h3'>Language</Header>
                <Label.Group>
                  <Label color='red'>{mdx.fields.pl}</Label>
                  {mdx.frontmatter.plVersion ? (
                    <>
                      {mdx.frontmatter.plVersion.from ? (
                        <Label
                          as={Link}
                          to={`/specs/${mdx.frontmatter.plVersion.from}/`}
                          color='yellow'
                          image
                        >
                          since
                          <Label.Detail>
                            {mdx.frontmatter.plVersion.from}
                          </Label.Detail>
                        </Label>
                      ) : null}
                      {mdx.frontmatter.plVersion.to ? (
                        <Label
                          as={Link}
                          to={`/specs/${mdx.frontmatter.plVersion.to}/`}
                          color='yellow'
                          image
                        >
                          until
                          <Label.Detail>
                            {mdx.frontmatter.plVersion.to}
                          </Label.Detail>
                        </Label>
                      ) : null}
                    </>
                  ) : null}
                </Label.Group>
                <Label.Group style={{ marginTop: "0.5rem" }}>
                  {mdx.frontmatter.spec
                    ? mdx.frontmatter.spec.map(spec =>
                        spec.sections.map(section => (
                          <Label
                            key={`spec-${spec.id}-${section}`}
                            as={Link}
                            to={`/specs/${spec.id}/${section}/`}
                            color='red'
                            data-tooltip={`${specVersionLabel(
                              specsMdx,
                              spec.id,
                              mdx.fields.pl
                            )} Specification`}
                          >
                            {spec.id}
                            <Label.Detail>{`${formatSpecSection(
                              section
                            )} ${getSpecSectionTitle(
                              specsMdx,
                              spec.id,
                              section
                            )}`}</Label.Detail>
                          </Label>
                        ))
                      )
                    : null}
                </Label.Group>
                {misconceptionConcepts.length === 0 ? (
                  ""
                ) : (
                  <>
                    <Header as='h3'>Concepts</Header>
                    <Label.Group>
                      {misconceptionConcepts.map((c, index) => (
                        <Label
                          key={`concepts-${index}`}
                          as={Link}
                          to={`/concepts/${_.kebabCase(c)}/${mdx.fields.pl}/`}
                          color='brown'
                        >
                          {_.startCase(c)}
                        </Label>
                      ))}
                    </Label.Group>
                  </>
                )}
                {mdx.frontmatter.expressibleInNms &&
                mdx.frontmatter.expressibleInNms.length !== 0 ? (
                  <>
                    <Header as='h3'>Expressible In</Header>
                    <List selection>
                      {mdx.frontmatter.expressibleInNms.map(nm => (
                        <List.Item
                          key={nm}
                          as={Link}
                          to={`/notionalMachines/${nm.id}/`}
                        >
                          <List.Icon
                            name='settings'
                            size='large'
                            verticalAlign='middle'
                          />
                          <List.Content>
                            <List.Header>
                              <span
                                style={{
                                  color: "#4183c4",
                                }}
                              >
                                {nm.id}
                              </span>
                            </List.Header>
                            <List.Description>{nm.how}</List.Description>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List>
                  </>
                ) : null}
                {mdx.frontmatter.related ? (
                  <>
                    <Header as='h3'>Related Misconceptions</Header>
                    <List selection verticalAlign='middle'>
                      {mdx.frontmatter.related.map((mc, index) => (
                        <List.Item
                          as={Link}
                          to={`/misconceptions/${mdx.fields.pl}/${mc.id}/`}
                          key={`related-${index}`}
                        >
                          <List.Icon
                            className='warning sign'
                            size='large'
                            verticalAlign='middle'
                          />
                          <List.Content>
                            <List.Header>
                              {/* color this like a link, without producing a nested a element */}
                              <span style={{ color: "#4183c4" }}>{mc.id}</span>
                            </List.Header>
                            <List.Description>{mc.relation}</List.Description>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List>
                  </>
                ) : null}
                {mdx.frontmatter.inOtherPl ? (
                  <>
                    <Header as='h3'>Other Languages</Header>
                    <List selection verticalAlign='middle'>
                      {mdx.frontmatter.inOtherPl.map((mc, index) => (
                        <List.Item
                          as={Link}
                          to={`/misconceptions/${mc.pl}/${mc.id}/`}
                          key={`related-${index}`}
                        >
                          <List.Icon
                            className='warning sign'
                            size='large'
                            verticalAlign='middle'
                          />
                          <List.Content>
                            <Label color='red' image>
                              {mc.pl}
                              <Label.Detail>{mc.id}</Label.Detail>
                            </Label>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List>
                  </>
                ) : null}
                {relatedRefs.length > 0 ? (
                  <>
                    <Header as='h3'>Literature References</Header>
                    <p>
                      The following papers directly or indirectly provide
                      qualitative or quantitative evidence related to this
                      misconception.
                    </p>
                    <List selection>
                      {relatedRefs.map(reference => (
                        <LiteratureReference
                          key={reference.fields.citationKey}
                          reference={reference}
                          refs={data.refs}
                          zoteroItems={zoteroItems}
                          misconception={{
                            lang: thisLang,
                            name: thisName,
                          }}
                        />
                      ))}
                    </List>
                  </>
                ) : null}
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    </Layout>
  );
}

export const pageQuery = graphql`
  query ($slug: String!) {
    mdx(fields: { slug: { eq: $slug } }) {
      body
      rawBody
      fields {
        name
        pl
        image {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
            resize(width: 1024, height: 512, fit: COVER, cropFocus: NORTH) {
              src
              height
            }
          }
        }
        correction {
          childMdx {
            body
            rawBody
          }
        }
        origin {
          childMdx {
            body
            rawBody
          }
        }
        symptoms {
          childMdx {
            body
            rawBody
          }
        }
        value {
          childMdx {
            body
            rawBody
          }
        }
      }
      frontmatter {
        shortDescription
        shortCorrection
        status
        observed
        pytamaroActivityVersion
        videoId
        concepts
        related {
          id
          relation
        }
        inOtherPl {
          pl
          id
        }
        plVersion {
          from
          to
        }
        expressibleInNms {
          id
          how
        }
        spec {
          id
          sections
        }
      }
    }

    specsMdx: allMdx(
      filter: { fileAbsolutePath: { glob: "**/src/data/specs/*.md" } }
    ) {
      nodes {
        parent {
          ... on File {
            name
          }
        }
        frontmatter {
          pl
          version
          sections {
            section
            title
            url
          }
        }
      }
    }

    concepts: allFile(
      filter: { absolutePath: { glob: "**/src/data/concepts/*.md" } }
    ) {
      nodes {
        name
      }
    }

    zoteroItems: allZoteroItems {
      nodes {
        key
        citationKey
        data {
          parsedDate
          creatorSummary
        }
      }
    }

    refs: allMdx(filter: { frontmatter: { type: { eq: "ref" } } }) {
      edges {
        node {
          id
          slug
          frontmatter {
            langs
            method
            subjects
            artifact {
              url
              note
            }
            phenomenonKinds {
              name
              description
              phenomena {
                id
                name
                observations {
                  kind
                  measure
                  count
                  maxCount
                  percent
                  counts
                  levelLabels
                  value
                  maxValue
                  maxLabel
                  minValue
                  minLabel
                  category
                  categoryDescription
                }
                plMisconceptions {
                  lang
                  name
                }
              }
            }
          }
          fields {
            slug
            citationKey
          }
        }
      }
    }
  }
`;
